import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowRight } from "@phosphor-icons/react/ArrowRight";
import { paths } from "config/paths";
import { motion, useScroll, useTime, useTransform } from "framer-motion";
import {
	MapField,
	mapPalettes,
	type MapFieldProps,
	type MapPalette,
} from "mangomap-ui/src/components/map-field";
import RouterLink from "next/link";
import * as React from "react";
import { headingFontFamily } from "../heading-font";
export interface HeroPanel {
	src: string;
	alt?: string;
	side: "left" | "right";
	verticalOffset: number;
	scrollRate: number;
}
export interface HeroCustomer {
	customer: string;
	src: string;
}
export interface HeroMapFieldConfig {
	seed?: string;
	variant?: MapFieldProps["variant"];
	/** `line` = regular lines, `index` = emphasised index lines. */
	palette?: MapPalette;
	strokeWidth?: number;
	aspect?: number | string;
	opacity?: number;
	/** Sliding emphasis animation across the value range. */
	animate?: boolean;
	duration?: number;
	/** Fractal-noise detail used by every variant. Higher = more intricate. */
	octaves?: number;
	/** Contour-only controls. */
	levels?: number;
	cols?: number;
	rows?: number;
	indexEvery?: number;
	/** Parcels-only controls. */
	parcelMinSize?: number;
	parcelMaxDepth?: number;
}
export interface HeroContent {
	eyebrow: string;
	icon: React.ReactElement;
	heading: string;
	subHeading: string;
	ctaButton: string;
	keyObjection: string;
	socialProof: string;
	socialProofAvatar?: string;
	socialProofLabel: string;
	image: string;
	customers: HeroCustomer[];
	mapField?: HeroMapFieldConfig;
}

const defaultHeroMapField: Required<HeroMapFieldConfig> = {
	seed: "mangohero",
	variant: "parcels",
	palette: mapPalettes.slate,
	strokeWidth: 0.5,
	aspect: "16 / 9",
	opacity: 0.12,
	animate: true,
	duration: 5,
	octaves: 3,
	levels: 11,
	cols: 72,
	rows: 48,
	indexEvery: 5,
	parcelMinSize: 0.5,
	parcelMaxDepth: 10,
};
export interface HeroProps {
	panels?: HeroPanel[];
	content: HeroContent;
}

const defaultLeftPanel: HeroPanel = {
	side: "left",
	verticalOffset: 48,
	scrollRate: 0.16,
	src: "/app-assets/images/hero-popup.png",
};

const defaultRightPanel: HeroPanel = {
	side: "right",
	verticalOffset: 88,
	scrollRate: 0.11,
	src: "/app-assets/images/hero-legend.png",
};

const defaultPanels: HeroPanel[] = [defaultLeftPanel, defaultRightPanel];

const leftToolbarOverlay = {
	src: "/app-assets/images/hero-tools.png",
};

const centerOverlay = {
	src: "/app-assets/images/hero-select.png",
};

const panelPlacement = {
	left: {
		left: { md: "-24px", lg: "-56px" },
	},
	right: {
		right: { md: "-24px", lg: "-56px" },
	},
} as const;

const mountTransition = {
	duration: 0.45,
	ease: [0.22, 1, 0.36, 1] as const,
};

export function Hero({ panels = defaultPanels, content: hero }: HeroProps) {
	const customers = hero.customers;

	const { scrollY } = useScroll();
	const time = useTime();
	const panelScale = useTransform(scrollY, [0, 800], [0.92, 1]);
	const mainImageY = useTransform(scrollY, [0, 800], [0, -28]);
	const mainImageRotateX = useTransform(scrollY, [0, 800], [0, 10]);
	const centerOverlayY = useTransform(scrollY, [0, 800], [18, 80]);
	const centerOverlayScale = useTransform(scrollY, [0, 800], [0.7, 0.94]);
	const centerOverlayRotateX = useTransform(scrollY, [0, 800], [1, -10]);
	const leftToolbarOverlayX = useTransform(scrollY, [0, 800], [-16, -56]);
	const leftToolbarOverlayY = useTransform(scrollY, [0, 800], [-226, -196]);
	const leftToolbarOverlayScale = useTransform(scrollY, [0, 800], [0.94, 1]);
	const leftToolbarOverlayRotateX = useTransform(scrollY, [0, 800], [2, 5]);
	const leftPanel = panels.find((panel) => panel.side === "left");
	const rightPanel = panels.find((panel) => panel.side === "right");
	const leftPanelX = useTransform(scrollY, [0, 800], [0, 18]);
	const rightPanelX = useTransform(scrollY, [0, 800], [0, -18]);
	const leftPanelY = useTransform(
		scrollY,
		[0, 800],
		[
			leftPanel?.verticalOffset ?? defaultLeftPanel.verticalOffset,
			(leftPanel?.verticalOffset ?? defaultLeftPanel.verticalOffset) -
				(leftPanel?.scrollRate ?? defaultLeftPanel.scrollRate) * 400,
		],
	);
	const rightPanelY = useTransform(
		scrollY,
		[0, 800],
		[
			rightPanel?.verticalOffset ?? defaultRightPanel.verticalOffset,
			(rightPanel?.verticalOffset ?? defaultRightPanel.verticalOffset) -
				(rightPanel?.scrollRate ?? defaultRightPanel.scrollRate) * 400,
		],
	);
	const leftToolbarOverlayHoverYTarget = useTransform(
		() => Math.sin(time.get() / 1400) * 4,
	);
	const leftToolbarOverlayHoverRotateTarget = useTransform(
		() => Math.sin(time.get() / 1600 + 0.45) * 0.75,
	);
	const leftPanelHoverYTarget = useTransform(
		() => Math.sin(time.get() / 1025 + 1.1) * 4,
	);
	const leftPanelHoverRotateTarget = useTransform(
		() => Math.sin(time.get() / 1250 + 0.8) * 0.75,
	);
	const centerOverlayHoverYTarget = useTransform(
		() => Math.sin(time.get() / 1300 + 2.15) * 6,
	);
	const centerOverlayHoverRotateTarget = useTransform(
		() => Math.sin(time.get() / 1500 + 1.65) * 1,
	);
	const rightPanelHoverYTarget = useTransform(
		() => Math.sin(time.get() / 960 + 2.8) * 4,
	);
	const rightPanelHoverRotateTarget = useTransform(
		() => Math.sin(time.get() / 1180 + 2.35) * 0.75,
	);
	// Drive the panel float directly from the bounded sin targets. A useSpring
	// integrator here destabilises under the irregular frame timing of a heavy
	// animated background (e.g. the contour hero), accumulating energy until the
	// panels fly off. A pure function of time cannot diverge.
	const leftToolbarOverlayYWithHover = useTransform(
		() => leftToolbarOverlayY.get() + leftToolbarOverlayHoverYTarget.get(),
	);
	const leftPanelYWithHover = useTransform(
		() => leftPanelY.get() + leftPanelHoverYTarget.get(),
	);
	const centerOverlayYWithHover = useTransform(
		() => centerOverlayY.get() + centerOverlayHoverYTarget.get(),
	);
	const rightPanelYWithHover = useTransform(
		() => rightPanelY.get() + rightPanelHoverYTarget.get(),
	);

	const mapField = { ...defaultHeroMapField, ...hero.mapField };

	return (
		<Box
			sx={{
				bgcolor: "var(--mui-palette-neutral-50)",
				color: "var(--mui-palette-common-white)",
				overflow: "hidden",
				position: "relative",
			}}>
			<Box
				aria-hidden
				sx={{
					position: "absolute",
					inset: 0,
					zIndex: 0,
					overflow: "hidden",
					pointerEvents: "none",
				}}>
				<MapField
					seed={mapField.seed}
					variant={mapField.variant}
					palette={mapField.palette}
					strokeWidth={mapField.strokeWidth}
					aspect={mapField.aspect}
					octaves={mapField.octaves}
					levels={mapField.levels}
					cols={mapField.cols}
					rows={mapField.rows}
					indexEvery={mapField.indexEvery}
					parcelMinSize={mapField.parcelMinSize}
					parcelMaxDepth={mapField.parcelMaxDepth}
					animate={mapField.animate}
					duration={mapField.duration}
					style={{ width: "100%", height: "100%", opacity: mapField.opacity }}
				/>
			</Box>
			<Container
				maxWidth="md"
				sx={{
					position: "relative",
					pt: "calc(100px + var(--MainNav-height))",
					pb: "40px",
					zIndex: 3,
				}}>
				<Stack spacing={6}>
					<Stack
						spacing={2}
						sx={{
							justifyContent: "center",
							alignItems: "center",
						}}>
						<div>
							<Chip
								color="secondary"
								icon={hero.icon}
								label={hero.eyebrow}
								size="small"
								variant="soft"
							/>
						</div>
						<Typography
							align="center"
							color="var(--mui-palette-common-black)"
							sx={{
								fontFamily: headingFontFamily,
								fontSize: "4rem",
								fontWeight: 500,
								lineHeight: 1.2,
							}}
							variant="h1">
							{hero.heading}
						</Typography>
						<Typography
							color="text.secondary"
							sx={{
								fontFamily: headingFontFamily,
								fontWeight: 500,
								textAlign: "center",
								fontSize: "1.2rem",
							}}
							variant="h2">
							{hero.subHeading}
						</Typography>
					</Stack>
					<Stack
						direction="column"
						spacing={1}
						sx={{ justifyContent: "center", alignItems: "center" }}>
						<div>
							<Button
								component={RouterLink}
								endIcon={<ArrowRight />}
								href={paths.signUp}
								size="large"
								variant="contained">
								{hero.ctaButton}
							</Button>
						</div>
						<Typography
							color="neutral.400"
							variant="caption">
							{hero.keyObjection}
						</Typography>
					</Stack>
				</Stack>
			</Container>
			<Container
				maxWidth="lg"
				sx={{ position: "relative", zIndex: 2 }}>
				<Box
					sx={{
						position: "relative",
						width: "100%",
					}}>
					<Box
						sx={{
							display: { xs: "none", md: "block" },
							maxWidth: "165px",
							position: "absolute",
							bottom: 0,
							left: { md: "16px", lg: "56px" },
							width: { md: "12%", lg: "13%" },
							zIndex: 2,
						}}>
						<motion.div
							animate={{ opacity: 1 }}
							initial={{ opacity: 0 }}
							transition={{ ...mountTransition, delay: 0.15 }}
							style={{
								perspective: "1000px",
								width: "100%",
							}}>
							<motion.img
								alt=""
								src={leftToolbarOverlay.src}
								style={{
									borderRadius: "20px",
									display: "block",
									height: "auto",
									rotateX: leftToolbarOverlayRotateX,
									rotateZ: leftToolbarOverlayHoverRotateTarget,
									scale: leftToolbarOverlayScale,
									width: "100%",
									x: leftToolbarOverlayX,
									y: leftToolbarOverlayYWithHover,
								}}
							/>
						</motion.div>
					</Box>
					{leftPanel ? (
						<Box
							sx={{
								display: { xs: "none", md: "block" },
								maxWidth: "460px",
								position: "absolute",
								bottom: 0,
								width: { md: "22%", lg: "35%" },
								zIndex: 3,
								...panelPlacement.left,
							}}>
							<motion.div
								animate={{ opacity: 1 }}
								initial={{ opacity: 0 }}
								transition={{ ...mountTransition, delay: 0.1 }}
								style={{
									perspective: "1000px",
									transformOrigin: "bottom right",
									width: "100%",
								}}>
								<motion.img
									alt={leftPanel.alt ?? ""}
									src={leftPanel.src}
									style={{
										borderRadius: "20px",
										display: "block",
										height: "auto",
										rotateX: 4,
										rotateZ: leftPanelHoverRotateTarget,
										scale: panelScale,
										width: "100%",
										x: leftPanelX,
										y: leftPanelYWithHover,
									}}
								/>
							</motion.div>
						</Box>
					) : null}
					<Box sx={{ position: "relative", zIndex: 1 }}>
						<motion.div
							animate={{ opacity: 1, y: 0 }}
							initial={{ opacity: 0, y: 16 }}
							transition={{ ...mountTransition, delay: 0 }}
							style={{ perspective: "1000px" }}>
							<motion.img
								alt={hero.eyebrow}
								src={hero.image}
								style={{
									boxShadow: "var(--mui-shadows-8)",
									borderRadius: "10px",
									display: "block",
									height: "auto",
									position: "relative",
									rotateX: mainImageRotateX,
									scale: panelScale,
									width: "100%",
									y: mainImageY,
								}}
							/>
						</motion.div>
					</Box>
					<Box
						sx={{
							display: { xs: "none", md: "block" },
							left: "50%",
							maxWidth: "446px",
							position: "absolute",
							top: { md: "42%", lg: "44%" },
							width: { md: "34%", lg: "36%" },
							zIndex: 2,
						}}>
						<motion.div
							animate={{ opacity: 1 }}
							initial={{ opacity: 0 }}
							transition={{ ...mountTransition, delay: 0.25 }}
							style={{
								perspective: "1200px",
								width: "100%",
								x: "-50%",
								y: "-50%",
							}}>
							<motion.img
								alt=""
								src={centerOverlay.src}
								style={{
									borderRadius: "20px",
									display: "block",
									height: "auto",
									rotateX: centerOverlayRotateX,
									rotateZ: centerOverlayHoverRotateTarget,
									scale: centerOverlayScale,
									width: "100%",
									y: centerOverlayYWithHover,
								}}
							/>
						</motion.div>
					</Box>
					{rightPanel ? (
						<Box
							sx={{
								display: { xs: "none", md: "block" },
								maxWidth: "269px",
								position: "absolute",
								bottom: 0,
								width: { md: "22%", lg: "25%" },
								zIndex: 3,
								...panelPlacement.right,
							}}>
							<motion.div
								animate={{ opacity: 1 }}
								initial={{ opacity: 0 }}
								transition={{ ...mountTransition, delay: 0.2 }}
								style={{
									perspective: "1000px",
									transformOrigin: "bottom left",
									width: "100%",
								}}>
								<motion.img
									alt={rightPanel.alt ?? ""}
									src={rightPanel.src}
									style={{
										borderRadius: "20px",
										display: "block",
										height: "auto",
										rotateX: 4,
										rotateZ: rightPanelHoverRotateTarget,
										scale: panelScale,
										width: "100%",
										x: rightPanelX,
										y: rightPanelYWithHover,
									}}
								/>
							</motion.div>
						</Box>
					) : null}
				</Box>
			</Container>
			<Box
				sx={{
					borderBottom: "1px solid var(--mui-palette-divider)",
					py: "64px",
					width: "100%",
				}}>
				<Box
					sx={{
						mx: "auto",
						maxWidth: "xl",
						px: { xs: 3, sm: 4, md: 6 },
						width: "100%",
					}}>
					<Stack spacing={3}>
						<Stack
							direction="row"
							spacing={1}
							sx={{
								alignItems: "center",
								justifyContent: "center",
							}}>
							{hero.socialProofAvatar ? (
								<Avatar
									alt=""
									src={hero.socialProofAvatar}
								/>
							) : null}
							<Typography
								color="neutral.400"
								sx={{ whiteSpace: "wrap" }}
								variant="body2">
								{hero.socialProof}
							</Typography>
						</Stack>
						<Stack sx={{ alignItems: "center" }}>
							<Typography
								color="text.secondary"
								variant="overline">
								{hero.socialProofLabel}
							</Typography>
						</Stack>
					</Stack>
				</Box>
				<Box
					sx={{
						color: "var(--mui-palette-neutral-900)",
						display: "flex",
						alignItems: "center",
						filter: "grayscale(1) opacity(0.6)",
						flexWrap: { xs: "wrap", lg: "nowrap" },
						justifyContent: { xs: "center", lg: "space-between" },
						columnGap: { xs: 4, sm: 5, md: 6, lg: 3 },
						rowGap: 4,
						px: { xs: 3, sm: 4, md: 6 },
						pt: 3,
						width: "100%",
					}}>
					{customers.map((customer) => (
						<Box
							alt={customer.customer}
							component="img"
							key={customer.customer}
							src={customer.src}
							sx={{
								flex: "0 0 auto",
								height: "35px",
							}}
						/>
					))}
				</Box>
			</Box>
		</Box>
	);
}
